import type { WalletSelectedDesign } from "@lemmonhq/types";
import { hc } from "hono/client";
import { LemmonCustomerAppType } from "../index";
import { createTRPCProxyClient, httpBatchLink, httpLink } from "@trpc/client";
import type { AppRouter } from "../trpc/router";
import {
  Cart,
  CustomerAddressEditable,
  CustomerCheckout,
  GroupOrder,
  Order,
  PaymentDetails,
  Promotion,
  PromotionReward,
  ReservationRequest,
  ReservationAvailability,
  RestaurantProfile,
  ShopProfile,
  Event,
  EventWithShop,
  CustomerProfileAddress,
  ProfileOrder,
} from "@lemmonhq/types";
//import { trackedFetch } from "./progressbar";

export const getClient = () => {
  const client = hc<LemmonCustomerAppType>("/", {
    //fetch: trackedFetch,
  });
  return client;
};
export type LemmonCustomerClientType = ReturnType<typeof getClient>;

export const getTrpcClient = () =>
  createTRPCProxyClient<AppRouter>({
    links: [
      httpLink({
        url: "/api/trpc",
      }),
    ],
  });

// Shared fetch wrapper
async function fetchJson<T>(req: Response) {
  return;
}

export const fetchPromotions = async (
  shopSlug: string,
): Promise<Promotion[]> => {
  const client = getClient();
  const req = await client.api.v3.customer.promotions.$get({
    query: { shopSlug },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};
export const fetchPayment = async (
  guid: string,
): Promise<{
  payment: PaymentDetails;
  shop: RestaurantProfile;
  orders: Order[];
}> => {
  const client = getClient();
  const req = await client.api.v3.customer.payments[":guid"].$get({
    param: { guid },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return (await req.json()) as unknown as {
    payment: PaymentDetails;
    shop: RestaurantProfile;
    orders: Order[];
  };
};

export type CoinOffer = {
  coin_program_id: string;
  coin_type: string;
  coin_label: string;
  name: string;
  icon: string | null;
  amount: number;
  validity_months: number | null;
  /** What the coins buy, cheapest first. */
  tiers: CollectedTier[];
};

export type CoinOfferResponse = {
  programs: CoinOffer[];
  claimed: boolean;
  signed_in: boolean;
};

export type CoinClaimResult = {
  status:
    | "claimed"
    | "already_claimed"
    | "not_claimable"
    | "window_expired"
    | "nothing_on_offer";
  claimed: number;
  programs: CoinOffer[];
};

/** What this receipt is worth in coins. Public — the offer precedes the signup. */
export const fetchCoinOffer = async (
  guid: string,
): Promise<CoinOfferResponse> => {
  const client = getClient();
  const req = await client.api.v3.customer.coins[":guid"].$get({
    param: { guid },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as unknown as CoinOfferResponse;
};

export type CollectedTier = {
  id: string;
  /** Coins this tier costs. */
  amount: number;
  reward: PromotionReward;
};

export type CollectedProgram = {
  coin_program_id: string;
  name: string;
  coin_label: string;
  coin_type: string;
  icon: string | null;
  balance: number;
  earned: number;
  redeemed: number;
  next_expiry: string | null;
  /** Cheapest first, so the app can find the next reachable reward. */
  tiers: CollectedTier[];
  /** The program's selected wallet pass design; the card is drawn from it. */
  design: WalletSelectedDesign | null;
};

export type CollectedPayment = {
  guid: string;
  created_at: string;
  amount: number;
  method: string | null;
  coins: number;
};

export type CollectedShop = {
  restaurant_id: number;
  /** Per-restaurant ledger owner; retained for shop-specific history. */
  customer_id: string;
  /** Global account identity encoded in loyalty QRs and resolved by the POS. */
  profile_id: string;
  name: string;
  slug: string | null;
  currency: string | null;
  balance: number;
  last_visit: string | null;
  programs: CollectedProgram[];
  payments: CollectedPayment[];
};

export type CollectedResponse = {
  totals: { coins: number; shops: number; payments: number };
  shops: CollectedShop[];
};

/** Everything this account has collected, grouped by restaurant. */
export const fetchCollected = async (): Promise<CollectedResponse> => {
  const client = getClient();
  const req = await client.api.v3.customer.coins.collected.$get();
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as unknown as CollectedResponse;
};

/** Mints the coins against the signed-in account. Requires a verified session. */
export const claimCoins = async (guid: string): Promise<CoinClaimResult> => {
  const client = getClient();
  const req = await client.api.v3.customer.coins[":guid"].claim.$post({
    param: { guid },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as unknown as CoinClaimResult;
};

export const fetchShop = async (slug: string): Promise<ShopProfile> => {
  const client = getClient();
  const req = await client.api.v3.customer.shop.shop[":slug"].$get({
    param: { slug },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const fetchGroupOrder = async (groupId: string): Promise<GroupOrder> => {
  const client = getClient();
  const req = await client.api.v3.customer["group-orders"][":groupId"].$get({
    param: { groupId },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const createGroupOrder = async (
  shopId: number,
  table_id?: number,
  type?: string,
): Promise<GroupOrder> => {
  const client = getClient();
  const req = await client.api.v3.customer["group-orders"].$post({
    json: {
      shop_id: shopId,
      table_id,
      type,
    },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const joinGroupOrder = async (
  groupId: string,
  customerId: string,
  payload?: Record<string, any>,
): Promise<{
  id: string;
  customer_id: string;
  payload: Record<string, any>;
  created_at: string;
  updated_at: string;
}> => {
  const client = getClient();
  const req = await (
    client.api.v3.customer["group-orders"][":groupId"]["join"] as any
  ).$post({
    param: { groupId },
    json: {
      customer_id: customerId,
      payload: payload || {},
    },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as {
    id: string;
    customer_id: string;
    payload: Record<string, any>;
    created_at: string;
    updated_at: string;
  };
};

export const fetchServices = async (shopSlug: string) => {
  const client = getClient();
  const req = await client.api.v3.customer.catalog[":shopSlug"].services.$get({
    param: { shopSlug },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const fetchProducts = async (shopSlug: string) => {
  const client = getClient();
  const req = await client.api.v3.customer.catalog[":shopSlug"].products.$get({
    param: { shopSlug },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const fetchCustomerSession = async () => {
  const client = getClient();
  const req = await client.api.v3.customer.session.me.$get();
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

/** Clears the session cookie. The server resets the caller to an anonymous session. */
export const logout = async () => {
  const client = getClient();
  const req = await client.api.v3.customer.session.sessions.$delete();
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const requestCreateLoginToken = async (
  email: string,
  signup?: {
    name?: string | null;
    birthDayMonth?: string | null;
    birthYear?: number | null;
  },
) => {
  const client = getClient();
  const req = await client.api.v3.customer.session.email_tokens.$post({
    json: {
      email,
      // Only sent when creating an account; ignored by the server for an
      // existing email (that path is a login).
      ...(signup?.name ? { name: signup.name } : {}),
      ...(signup?.birthDayMonth
        ? { birth_day_month: signup.birthDayMonth }
        : {}),
      ...(signup?.birthYear ? { birth_year: signup.birthYear } : {}),
    },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const verifyLoginCode = async (email: string, code: string) => {
  const client = getClient();
  const req = await client.api.v3.customer.session.email_tokens.verify.$post({
    json: {
      email,
      code,
    },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export type GoogleOneTapConfig = {
  clientId: string;
  state: string;
  nonce: string;
};

/** Returns null when Google sign-in isn't configured on the server. */
export const fetchGoogleOneTapConfig =
  async (): Promise<GoogleOneTapConfig | null> => {
    const client = getClient();
    const req = await client.api.oauth.google["one-tap"].$get();
    if (!req.ok) return null;
    return (await req.json()) as GoogleOneTapConfig;
  };

export const signInWithGoogleCredential = async (
  credential: string,
  state: string,
) => {
  const client = getClient();
  const req = await client.api.oauth.google["one-tap"].$post({
    json: { credential, state },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const fetchCustomerAddresses = async (): Promise<
  CustomerProfileAddress[]
> => {
  const client = getClient();
  const req = await client.api.v3.customer.addresses.$get({});
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as CustomerProfileAddress[];
};

export const deleteCustomerAddress = async (id: string) => {
  const client = getClient();
  const req = await client.api.v3.customer.addresses[":id"].$delete({
    param: { id },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return true;
};

export const createCustomerAddress = async (
  address: CustomerAddressEditable,
) => {
  const client = getClient();
  const req = await client.api.v3.customer.addresses.$post({
    json: address,
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as unknown as CustomerProfileAddress;
};

export const createCustomerCheckout = async (
  slug: string,
  cart: Cart,
  service_method: string,
  payment_method: string,
  order_type: "grouped" | "single",
  group_id?: string,
  table_id?: number,
): Promise<{ id: string }> => {
  const client = getClient();
  const req = await client.api.v3.customer.checkouts.$post({
    json: {
      cart,
      group_id,
      table_id,
      service_method,
      payment_method,
      order_type,
      tips: cart.tips?.value,
      pickup_details: cart.pickupDetails,
      address_id: cart.addressId,
      order_notes: cart.orderNotes,
      order_schedule: cart.schedule,
    },
    query: { slug },
  });
  if (!req.ok) {
    // Optional: map status code to readable message here
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const fetchCheckout = async (
  checkoutId: string,
): Promise<{ checkout: CustomerCheckout; payment?: PaymentDetails | null }> => {
  const client = getClient();
  const req = await client.api.v3.customer.checkouts[":id"].$get({
    param: { id: checkoutId },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const fetchCheckoutFamily = async (
  checkoutId: string,
): Promise<CustomerCheckout[]> => {
  const client = getClient();
  const req = await client.api.v3.customer.checkouts.checkout_family.$get({
    query: { id: checkoutId },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return (await req.json()) as unknown as CustomerCheckout[];
};

export const finalizeCustomerCheckouts = async (attrs: {
  checkoutIds: string[];
  serviceMethod: string;
  tableId?: number;
  shopSlug: string;
  groupId: string;
}) => {
  const client = getClient();
  const req = await client.api.v3.customer.checkouts.finalize_v2.$post({
    json: {
      checkout_ids: attrs.checkoutIds,
      service_method: attrs.serviceMethod,
      table_id: attrs.tableId,
      shop_slug: attrs.shopSlug,
      group_id: attrs.groupId,
    },
  });
  if (!req.ok) {
    throw new Error(req.status.toString());
  }
  return await req.json();
};

export const createCustomerOrder = async (
  checkoutId: string,
): Promise<Order> => {
  const client = getClient();
  const res = await client.api.v3.customer.checkouts.consumer_order.$post({
    json: {
      checkout_id: checkoutId,
    },
  });
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("unable to create");
  }
};

export const fetchOrder = async (
  shopSlug: string,
  orderId: number,
): Promise<Order> => {
  const client = getClient();
  const res = await client.api.v3.customer.orders[":shopSlug"][":orderId"].$get(
    {
      param: { shopSlug, orderId: orderId + "" },
    },
  );
  if (res.ok) {
    return (await res.json()) as Order;
  } else {
    throw new Error("failed");
  }
};

export const fetchProfileOrders = async (): Promise<ProfileOrder[]> => {
  const client = getClient();
  const res = await client.api.v3.customer.orders.$get();
  if (!res.ok) throw new Error("failed");
  const body = await res.json();
  return "data" in body ? (body.data as ProfileOrder[]) : [];
};

export const createReservationRequest = async (data: {
  shopSlug: string;
  customer_id: string;
  requested_time: string;
  party_size: number;
  duration_mins?: number;
  notes?: string;
  party_name: string;
  party_email: string;
  party_phone: string;
  tag_ids?: string[];
  deposit_amount?: number;
  request_kind?: "new" | "edit";
  target_reservation_id?: string;
}): Promise<ReservationRequest> => {
  const client = getClient();
  const res = await client.api.v3.customer["reservation-requests"].$post({
    json: data,
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as unknown as ReservationRequest;
};

export const requestReservationChange = async (
  shopSlug: string,
  requestId: string,
  data: { requested_time: string; duration_mins?: number },
): Promise<ReservationRequest> => {
  const client = getClient();
  const res = await (
    client.api.v3.customer["reservation-requests"][":shopSlug"][":id"] as any
  )["request-change"].$post({
    param: { shopSlug, id: requestId },
    json: data,
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as ReservationRequest;
};

export const initiateDeposit = async (data: {
  shopSlug: string;
  requestedTime: string;
  partySize: number;
  durationMins?: number;
  notes?: string;
  partyName: string;
  partyEmail: string;
  partyPhone: string;
  tagIds?: string[];
  requestKind?: "new" | "edit";
  targetReservationId?: string;
}): Promise<{ workflowId: string; checkoutId: string; paymentId: number }> => {
  const trpc = getTrpcClient();
  return trpc.integratedCustomerPaymentWorkflow.create.mutate({
    subjectType: "reservation_deposit",
    shopSlug: data.shopSlug,
    requestedTime: data.requestedTime,
    partySize: data.partySize,
    durationMins: data.durationMins,
    notes: data.notes,
    partyName: data.partyName,
    partyEmail: data.partyEmail,
    partyPhone: data.partyPhone,
    tagIds: data.tagIds,
    requestKind: data.requestKind,
    targetReservationId: data.targetReservationId,
  });
};

export const fetchReservationRequest = async (
  shopSlug: string,
  id: string,
): Promise<ReservationRequest & { reservation?: any }> => {
  const client = getClient();
  const res = await client.api.v3.customer["reservation-requests"][":shopSlug"][
    ":id"
  ].$get({
    param: { shopSlug, id },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as unknown as ReservationRequest & {
    reservation?: any;
  };
};

export interface CancelCheckResult {
  is_after_deadline: boolean;
  has_deposit: boolean;
  deposit_amount: number;
  will_be_charged: boolean;
  deadline_hours: number;
  reservation_time: string;
  deadline_time: string;
}

export const fetchCancelCheck = async (
  shopSlug: string,
  id: string,
): Promise<CancelCheckResult> => {
  const client = getClient();
  const res = await client.api.v3.customer["reservation-requests"][":shopSlug"][
    ":id"
  ]["cancel-check"].$get({
    param: { shopSlug, id },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as CancelCheckResult;
};

export const cancelReservationRequest = async (
  shopSlug: string,
  id: string,
): Promise<ReservationRequest> => {
  const client = getClient();
  const res = await client.api.v3.customer["reservation-requests"][":shopSlug"][
    ":id"
  ].cancel.$post({
    param: { shopSlug, id },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as unknown as ReservationRequest;
};

export const fetchReservationTags = async (
  shopSlug: string,
): Promise<any[]> => {
  const client = getClient();
  const res = await client.api.v3.customer.reservations[":shopSlug"].tags.$get({
    param: { shopSlug },
  });
  if (!res.ok) {
    return [];
  }
  return (await res.json()) as any[];
};

export const cancelReservation = async (
  shopSlug: string,
  id: string,
): Promise<any> => {
  const client = getClient();
  const res = await client.api.v3.customer.reservations[":shopSlug"][
    ":id"
  ].cancel.$post({
    param: { shopSlug, id },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return await res.json();
};

export const fetchReservationAvailability = async (
  shopSlug: string,
  date: string,
  targetPartySize?: number,
  slots?: string[],
): Promise<ReservationAvailability> => {
  const client = getClient();
  const query: {
    date: string;
    target_party_size?: string | number;
    slots?: string;
  } = {
    date,
  };
  if (targetPartySize !== undefined) {
    query.target_party_size = targetPartySize;
  }
  if (slots && slots.length > 0) {
    query.slots = slots.join(",");
  }
  const res = await client.api.v3.customer.reservations[
    ":shopSlug"
  ].availability.$get({
    param: { shopSlug },
    query,
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as ReservationAvailability;
};

export type GalleryAsset = {
  id: string;
  restaurant_id: number;
  type: string;
  directory: string;
  meta: Record<string, unknown>;
  created_at: string;
  url: string;
};

export const fetchGalleryAssets = async (
  shopSlug: string,
): Promise<GalleryAsset[]> => {
  const client = getClient();
  const res = await client.api.v3.customer.shop.shop[":slug"].gallery.$get({
    param: { slug: shopSlug },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  const data = (await res.json()) as { data: GalleryAsset[] };
  return data.data;
};

// Eats Guide Types
export interface EatsGuideCity {
  slug: string;
  name: string;
  country: string;
  flag: string;
}

export interface RestaurantSetting {
  key: string;
  value: string | null;
  value_type: string | null;
}

export interface EatsGuideRestaurant {
  identifier: string;
  slug: string | null;
  name: { en: string | null };
  textTeaser: { en: string | null };
  description: { en: string | null };
  image: { url: string };
  gallery: Array<{ url: string; caption: { en: string | null } }>;
  address: {
    streetAddress: string;
    addressLocality: string;
    postalCode: string;
    telephone: string | null;
    email: string | null;
    url: string | null;
  };
  category: Record<string, unknown>;
  openingHours: string | string[];
  detailedInformation: { en: string[] };
  geoCoordinates: {
    latitude: number;
    longitude: number;
  } | null;
  settings?: RestaurantSetting[];
  timezone?: string | null;
  currency?: string | null;
  locale?: string | null;
  features?: string[];
  googleRating?: number | null;
  googleReviewCount?: number | null;
}

export interface DetectedCity {
  city: string;
  name: string;
  country: string;
  flag: string;
  detected_from: "city" | "country" | "default";
}

export interface BrandingProfile {
  id: string;
  name: string;
  subtitle: string;
  cities: EatsGuideCity[];
}

export interface BrandingResponse {
  branding: BrandingProfile;
  defaultCity: {
    city: string;
    name: string;
    country: string;
    flag: string;
  };
}

// Eats Guide API Functions
export const fetchBranding = async (): Promise<BrandingResponse> => {
  const client = getClient();
  const params = new URLSearchParams(window.location.search);
  const query: Record<string, string> = {};
  if (params.has("javshani")) query.javshani = "true";
  const res = await client.api.v3.customer["eats-guide"].branding.$get({
    query,
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as BrandingResponse;
};

export const fetchDetectedCity = async (): Promise<DetectedCity> => {
  const client = getClient();
  const res = await client.api.v3.customer["eats-guide"]["detect-city"].$get();
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as DetectedCity;
};

export const fetchSupportedCities = async (): Promise<EatsGuideCity[]> => {
  const client = getClient();
  const res = await client.api.v3.customer["eats-guide"].cities.$get();
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  const data = (await res.json()) as { data: EatsGuideCity[] };
  return data.data;
};

export const fetchEatsGuideRestaurants = async (
  city: string,
): Promise<{ data: EatsGuideRestaurant[]; city: EatsGuideCity }> => {
  const client = getClient();
  const res = await client.api.v3.customer["eats-guide"].restaurants[
    ":city"
  ].$get({
    param: { city },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as {
    data: EatsGuideRestaurant[];
    city: EatsGuideCity;
  };
};

export const fetchEatsGuideRestaurant = async (
  city: string,
  slug: string,
): Promise<{ data: EatsGuideRestaurant; city: EatsGuideCity }> => {
  const client = getClient();
  const res = await client.api.v3.customer["eats-guide"].restaurant[":city"][
    ":slug"
  ].$get({
    param: { city, slug },
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as {
    data: EatsGuideRestaurant;
    city: EatsGuideCity;
  };
};

// Events API Functions
export const fetchEvents = async (
  citySlug: string,
): Promise<EventWithShop[]> => {
  const client = getClient();
  const res = await client.api.v3.customer.events.city[":city"].$get({
    param: { city: citySlug },
    query: {},
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as EventWithShop[];
};

// Events API Functions
export const fetchShopEvents = async (
  shopSlug: string,
): Promise<EventWithShop[]> => {
  const client = getClient();
  const res = await client.api.v3.customer.events.shop[":shopSlug"].$get({
    param: { shopSlug: shopSlug },
    query: {},
  });
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as EventWithShop[];
};

export const fetchEvent = async (
  shopSlug: string,
  id: string,
): Promise<EventWithShop> => {
  const client = getClient();
  const res = await client.api.v3.customer.events.shop[":shopSlug"][":id"].$get(
    {
      param: { shopSlug, id },
    },
  );
  if (!res.ok) {
    throw new Error(res.status.toString());
  }
  return (await res.json()) as EventWithShop;
};

// Online payment workflow functions
export const invokeOnlinePaymentWorkflow = async (data: {
  checkoutId: string;
  amount?: number;
}): Promise<{
  invocationId: string;
  checkoutId: string;
  paymentId: number;
}> => {
  const trpc = getTrpcClient();
  const resp = await trpc.integratedCustomerPaymentWorkflow.create.mutate({
    subjectType: "group_order",
    checkoutId: data.checkoutId,
  });
  return {
    invocationId: resp.workflowId,
    checkoutId: resp.checkoutId,
    paymentId: resp.paymentId,
  };
};

export const getOnlinePaymentWorkflowStatus = async (
  invocationId: string,
): Promise<{
  status: string;
  output?: {
    status: string;
    paymentId?: number;
    providerId?: string;
    paymentUrl?: string;
    redirectPath?: string;
    error?: string;
  };
  error?: string;
}> => {
  const trpc = getTrpcClient();
  const resp = await trpc.integratedCustomerPaymentWorkflow.getStatus.query({
    workflowId: invocationId,
  });
  return resp;
};
